/**
 * Closeout card: "Add PO #" (2026-10-20).
 *
 * A location ticked "Needs a PO before we bill" and a job there with no PO
 * number used to show a green Send packet next to "Waiting on customer — PO".
 * The card's button is now Add PO #. It opens this small field right on the
 * card and saves the number to the job's own PO field (`jobs.customer_po`,
 * the same one the job page's PO box writes, via PATCH /api/jobs/:id
 * { customerPo }). Once it saves, the queue reloads and Send packet is back.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { refreshCloseOutQueue } from "./queueRefresh";

export function AddPoInline({ jobId, onClose }: { jobId: string; onClose: () => void }) {
    const qc = useQueryClient();
    const [po, setPo] = useState("");
    const save = useMutation({
        mutationFn: (value: string) => api.patch(`/api/jobs/${jobId}`, { customerPo: value }),
        onSuccess: () => {
            toast.success("PO number saved");
            refreshCloseOutQueue(qc, { now: true });
            onClose();
        },
        onError: () => toast.error("Couldn't save the PO number. Please try again."),
    });
    const value = po.trim();
    const submit = () => {
        if (value && !save.isPending) save.mutate(value);
    };
    return (
        // The card itself opens the job on click — keep typing and clicks here.
        <form
            className="flex min-w-0 flex-1 items-center gap-1.5"
            data-testid="closeout-add-po-form"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") onClose();
            }}
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
        >
            <input
                autoFocus
                value={po}
                onChange={(e) => setPo(e.target.value)}
                maxLength={120}
                placeholder="PO number"
                aria-label="PO number"
                className="h-7 min-w-0 flex-1 rounded-md border border-black/15 bg-white px-2 text-[12px] text-black focus:outline-none focus:ring-2 focus:ring-black/20"
            />
            <Button
                type="submit"
                size="sm"
                className="h-7 px-3 text-[12px] font-semibold"
                disabled={!value || save.isPending}
            >
                {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[12px]"
                onClick={onClose}
                disabled={save.isPending}
            >
                Cancel
            </Button>
        </form>
    );
}
